import { Component, useEffect, useState, type CSSProperties, type ErrorInfo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  Download,
  Gift,
  Home,
  Landmark,
  Leaf,
  Map,
  ScrollText,
  Settings,
  Shield,
  ShoppingBasket,
  Trash2,
  Volume2,
  VolumeX,
  Wind,
  X
} from "lucide-react";
import { formatMoney, normalizeMoney, projectRatio } from "@tribe/domain";
import { connectVillagePresence, deleteAccount, downloadAccountExport, initializeActivity, loadBootstrap } from "./api";
import { ActivePanel } from "./Panels";
import { useVillageStore, type PanelId, type Quality } from "./store";
import { VoxelWorld } from "./world/VoxelWorld";

const navigation: Array<{ id: Exclude<PanelId, null | "admin">; label: string; icon: typeof Map }> = [
  { id: "village", label: "Village", icon: Map },
  { id: "home", label: "Home", icon: Home },
  { id: "quests", label: "Quests", icon: ScrollText },
  { id: "shop", label: "Shop", icon: ShoppingBasket },
  { id: "collection", label: "Collection", icon: BookOpen },
  { id: "gifts", label: "Gifts", icon: Gift },
  { id: "project", label: "Project", icon: Landmark }
];

class SceneBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error("Village scene fallback", error, info.componentStack); }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="webgl-fallback">
        <Leaf size={34} />
        <span className="eyebrow">Simple village view</span>
        <h1>Your home is still here</h1>
        <p>The 3D village could not start on this device. Quests, rewards, your property, and the Town Square remain available.</p>
        <div><button className="primary-button" onClick={() => useVillageStore.getState().setPanel("home")}>Open my home</button><button className="secondary-button" onClick={() => useVillageStore.getState().setPanel("quests")}>View quests</button></div>
      </div>
    );
  }
}

function CoinStrip() {
  const copper = useVillageStore((state) => state.data.wallet.copper);
  const money = normalizeMoney(copper);
  return (
    <div className="coin-strip" aria-label={`Wallet ${formatMoney(copper)}`}>
      <span><i className="coin coin-gold" />{money.gold}</span>
      <span><i className="coin coin-silver" />{money.silver}</span>
      <span><i className="coin coin-copper" />{money.copper}</span>
    </div>
  );
}

/** The community project, as a progress ring rather than a bar across the top of the scene. */
function ProjectChip() {
  const project = useVillageStore((state) => state.data.project);
  const setPanel = useVillageStore((state) => state.setPanel);
  const percent = Math.round(projectRatio(project) * 100);
  return (
    <button className="project-chip" onClick={() => setPanel("project")} aria-label={`${project.name} ${percent} percent complete`}>
      <span className="project-ring" style={{ "--project-progress": `${percent}%` } as CSSProperties}>
        <Landmark size={14} />
      </span>
      <span><strong>{project.name}</strong><small>{percent}%</small></span>
    </button>
  );
}

function NavigationDock() {
  const panel = useVillageStore((state) => state.panel);
  const setPanel = useVillageStore((state) => state.setPanel);
  return (
    <nav className="navigation-dock glass" aria-label="Village navigation">
      {navigation.map((entry) => {
        const Icon = entry.icon;
        const active = panel === entry.id;
        return (
          <button key={entry.id} className={active ? "is-active" : ""} aria-label={entry.label} aria-current={active ? "page" : undefined} onClick={() => setPanel(active ? null : entry.id)}>
            <Icon size={20} strokeWidth={2} />
            {/* Only the active tab carries its label. Seven permanent captions was the
                largest piece of chrome competing with the village itself. */}
            {active && <span>{entry.label}</span>}
          </button>
        );
      })}
    </nav>
  );
}

function SettingsPanel({ close }: { close: () => void }) {
  const quality = useVillageStore((state) => state.quality);
  const setQuality = useVillageStore((state) => state.setQuality);
  const reducedMotion = useVillageStore((state) => state.reducedMotion);
  const setReducedMotion = useVillageStore((state) => state.setReducedMotion);
  const ambienceVolume = useVillageStore((state) => state.ambienceVolume);
  const setAmbienceVolume = useVillageStore((state) => state.setAmbienceVolume);
  const options: Quality[] = ["low", "balanced", "high"];
  const percent = Math.round(ambienceVolume * 100);
  // So that muting and unmuting returns the member to the level they had chosen, rather than
  // silently resetting them to the default every time.
  const [lastAudible, setLastAudible] = useState(ambienceVolume || 0.35);
  const toggleMute = () => {
    if (ambienceVolume > 0) {
      setLastAudible(ambienceVolume);
      setAmbienceVolume(0);
    } else {
      setAmbienceVolume(lastAudible || 0.35);
    }
  };
  return (
    <aside className="settings-popover" aria-label="Village settings">
      <header><div><span className="eyebrow">Display</span><h2>Village settings</h2></div><button className="icon-button" onClick={close} aria-label="Close settings"><X size={19} /></button></header>
      <label className="field-label">3D quality</label>
      <div className="quality-switch" role="radiogroup" aria-label="3D quality">
        {options.map((option) => <button key={option} className={quality === option ? "is-active" : ""} aria-pressed={quality === option} onClick={() => setQuality(option)}>{option}</button>)}
      </div>
      <label className="field-label" htmlFor="ambience-volume">Village sound</label>
      <div className="volume-row">
        <button className="icon-button" onClick={toggleMute} aria-label={ambienceVolume > 0 ? "Mute village sound" : "Unmute village sound"}>
          {ambienceVolume > 0 ? <Volume2 size={19} /> : <VolumeX size={19} />}
        </button>
        <input
          id="ambience-volume"
          type="range"
          min={0}
          max={100}
          step={1}
          value={percent}
          onChange={(event) => setAmbienceVolume(Number(event.target.value) / 100)}
        />
        <small>{percent === 0 ? "Off" : `${percent}%`}</small>
      </div>
      <button className="setting-row" role="switch" aria-checked={reducedMotion} onClick={() => setReducedMotion(!reducedMotion)}>
        <Wind size={20} />
        <span><strong>Reduced motion</strong><small>Calms villagers, plants, and camera effects</small></span>
        <i className={reducedMotion ? "is-on" : ""} />
      </button>
      <YourData />
    </aside>
  );
}

/**
 * Export and deletion, where a member can actually reach them.
 *
 * Both routes existed on the Worker and nothing in the Activity called either, which made the
 * privacy policy's promise of them true only for somebody willing to write their own request.
 */
function YourData() {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useVillageStore((state) => state.showToast);

  const exportData = async () => {
    setBusy(true);
    try {
      const wrote = await downloadAccountExport();
      toast(wrote ? "Your village data is downloading" : "Export is available once the village is connected", wrote ? "success" : "info");
    } catch (error) {
      toast(error instanceof Error ? error.message : "That export could not be prepared", "error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const removed = await deleteAccount();
      if (!removed) return toast("Deletion is available once the village is connected", "info");
      toast("Your account has been removed", "success");
      window.location.reload();
    } catch (error) {
      toast(error instanceof Error ? error.message : "That account could not be removed", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="settings-data">
      <label className="field-label">Your data</label>
      <button className="setting-row" onClick={() => void exportData()} disabled={busy}>
        <Download size={20} />
        <span><strong>Export everything</strong><small>Your property, wallet ledger, gifts, and quest history</small></span>
      </button>
      {!confirming ? (
        <button className="setting-row is-destructive" onClick={() => setConfirming(true)} disabled={busy}>
          <Trash2 size={20} />
          <span><strong>Delete my account</strong><small>Removes your identity and keeps what you built, anonymously</small></span>
        </button>
      ) : (
        <div className="delete-confirm">
          {/* Typed rather than clicked. This is the one control in the Activity that cannot be
              undone, and a second button beside the first is not a decision. */}
          <p>This cannot be undone. Type <strong>delete</strong> to confirm.</p>
          <input aria-label="Type delete to confirm" value={typed} onChange={(event) => setTyped(event.target.value)} autoComplete="off" />
          <div>
            <button className="secondary-button" onClick={() => { setConfirming(false); setTyped(""); }}>Cancel</button>
            <button className="danger-button" disabled={typed.trim().toLowerCase() !== "delete" || busy} onClick={() => void remove()}>Delete</button>
          </div>
        </div>
      )}
      <p className="settings-footnote">
        The village records which Discord messages completed a quest, never what they said.
      </p>
    </section>
  );
}

function ToastMessage() {
  const toast = useVillageStore((state) => state.toast);
  const dismiss = useVillageStore((state) => state.dismissToast);
  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(dismiss, 4200);
    return () => window.clearTimeout(timeout);
  }, [toast, dismiss]);
  if (!toast) return null;
  return <div className={`toast toast-${toast.tone}`} role="status"><Leaf size={19} /><span>{toast.message}</span><button onClick={dismiss} aria-label="Dismiss"><X size={17} /></button></div>;
}

function LoadingVillage() {
  return (
    <main className="loading-village">
      <div className="loading-mark"><span /><span /><span /></div>
      <p className="eyebrow">Tiny Tribe Village</p>
      <h1>Waking the village</h1>
      <p>Setting out paths, gardens, and warm windows.</p>
    </main>
  );
}

export function App() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mode, setMode] = useState<"discord" | "demo">("demo");
  const setBootstrap = useVillageStore((state) => state.setBootstrap);
  const panel = useVillageStore((state) => state.panel);
  const setPanel = useVillageStore((state) => state.setPanel);
  const data = useVillageStore((state) => state.data);
  const selectedPlacementId = useVillageStore((state) => state.selectedPlacementId);
  const receivePresence = useVillageStore((state) => state.receivePresence);
  const query = useQuery({
    queryKey: ["village-bootstrap"],
    queryFn: async () => {
      const activity = await initializeActivity({
        onThermalState: (state) => { if (state >= 2) useVillageStore.getState().setQuality("low"); },
        onLayoutMode: (layoutMode) => { document.documentElement.dataset.discordLayout = String(layoutMode); }
      });
      setMode(activity.mode);
      return loadBootstrap();
    }
  });

  useEffect(() => {
    if (query.data) setBootstrap(query.data);
  }, [query.data, setBootstrap]);

  useEffect(() => query.data ? connectVillagePresence(receivePresence) : undefined, [query.data, receivePresence]);

  if (query.isPending) return <LoadingVillage />;
  if (query.isError) {
    return (
      <main className="error-village">
        <Leaf size={32} />
        <span className="eyebrow">The gate is stuck</span>
        <h1>The village did not open</h1>
        <p>{query.error instanceof Error ? query.error.message : "Please try again in a moment."}</p>
        <button className="primary-button" onClick={() => void query.refetch()}>Try again</button>
      </main>
    );
  }

  return (
    <main className="activity-shell" id="app-controls">
      <section className="world-canvas" aria-label="Interactive Tiny Tribe Village">
        <SceneBoundary><VoxelWorld /></SceneBoundary>
      </section>
      {/* Two objects, not five. Everything permanently on screen is either the brand button
          or the one cluster on the right, which leaves the middle of the frame to the town. */}
      <header className="top-bar">
        <button className="brand glass" onClick={() => { setPanel(null); useVillageStore.getState().selectPlot(data.property.plotId); }} aria-label="Go to my home">
          <span className="brand-mark"><Home size={18} /></span>
          <span><strong>Tiny Tribe</strong><small>{mode === "demo" ? "Private preview" : data.guild.name}</small></span>
        </button>
        <div className="top-cluster glass">
          <ProjectChip />
          <i className="cluster-divider" />
          <CoinStrip />
          {data.guild.isModerator && (
            <>
              <i className="cluster-divider" />
              <button className={panel === "admin" ? "cluster-action is-active" : "cluster-action"} onClick={() => setPanel(panel === "admin" ? null : "admin")} aria-label="Moderator tools"><Shield size={18} /></button>
            </>
          )}
          <i className="cluster-divider" />
          <button className={settingsOpen ? "cluster-action is-active" : "cluster-action"} onClick={() => setSettingsOpen(!settingsOpen)} aria-label="Settings"><Settings size={18} /></button>
        </div>
      </header>
      {!selectedPlacementId && <div className="scene-tip"><span>Drag to explore</span><i /> <span>Scroll to zoom</span></div>}
      {panel && <ActivePanel panel={panel} />}
      {settingsOpen && <SettingsPanel close={() => setSettingsOpen(false)} />}
      {/* Arranging one thing is a single task, and its controls are on the item itself, so the
          dock stands down for the duration and leaves the yard the only thing on screen. */}
      {!selectedPlacementId && <NavigationDock />}
      <ToastMessage />
    </main>
  );
}
